/* eslint-disable @typescript-eslint/no-explicit-any */
import { AfterViewInit, Component, ElementRef, Input, OnDestroy } from '@angular/core';
import { LogService } from '@app/core/services/log.service';
import { BaseInputComponent } from '@app/modules/inputs/components/base-input/base-input.component';
import { InputCheckboxComponent } from '@app/modules/inputs/components/input-checkbox/input-checkbox.component';
import { InputRadioComponent } from '@app/modules/inputs/components/input-radio/input-radio.component';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { merge, Subject, takeUntil, tap } from 'rxjs';

/**
 * Currently does not support the following cases:
 * Input groups with button with dropdown addons https://getbootstrap.com/docs/5.0/forms/input-group/#buttons-with-dropdowns
 * Input groups with segmented buttons https://getbootstrap.com/docs/5.0/forms/input-group/#segmented-buttons
 * Input groups custom forms https://getbootstrap.com/docs/5.0/forms/input-group/#custom-forms
 */

@Component({
    selector: 'fdx-label',
    templateUrl: './label.component.html',
    styleUrl: './label.component.scss',
    standalone: false
})
export class LabelComponent implements AfterViewInit, OnDestroy {

    @Input() labelText: string;   // The label text. Unless defined as something different, will be equal to string value of ng-content, so the label should not be styled within the fdx-label tag without passing in a plain text labelText value.

    /** The input associated with this label, passed in from form-field.component. Used for styling the label. */
    public input: BaseInputComponent | undefined;

    /** classes handles the following cases:
     * https://getbootstrap.com/docs/5.0/forms/checks-radios/#toggle-buttons and https://getbootstrap.com/docs/5.0/forms/checks-radios/#radio-toggle-buttons (will remove .form-check-label if 'btn' is included)
     * https://getbootstrap.com/docs/5.0/forms/checks-radios/#outlined-styles
     * https://getbootstrap.com/docs/5.0/forms/input-group/#basic-example
     * https://getbootstrap.com/docs/5.0/forms/input-group/#wrapping
     * https://getbootstrap.com/docs/5.0/forms/input-group/#sizing
     * https://getbootstrap.com/docs/5.0/forms/floating-labels/#example
     */
    @Input() classes: Record<string, boolean> = {};
    @Input() styles: Record<string, any> = {};

    private initClasses: Record<string, boolean> = {};
    protected selectionIcon?: IconDefinition;

    private readonly unsubscribe$: Subject<void> = new Subject();

    constructor(
        private readonly element: ElementRef<HTMLElement>,
        private readonly logService: LogService
    ) {}

    ngAfterViewInit(): void {
        this.validateConfiguration();

        const input = this.input;
        if (!input) {
            return;
        }

        if (!this.labelText) {
            this.labelText = this.element.nativeElement.children[0].textContent?.trim() ?? '';
        }

        setTimeout(() => {
            input.labelText = this.labelText;
        });

        if (input.inputType === 'checkbox' || input.inputType === 'radio') {
            if (this.classes['btn']) {   // Must subscribe to changes to update label styles when using toggle buttons
                if (input instanceof InputCheckboxComponent || input instanceof InputRadioComponent) {
                    this.bindSetActive(input);
                }
            } else {
                this.initClasses['form-check-label'] = true;
            }
        } else if (this.classes['col-form-label'] === undefined) {
            this.initClasses['col-form-label'] = true;
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private isActive(input: InputCheckboxComponent | InputRadioComponent): boolean {
        if (input instanceof InputRadioComponent) {
            return input.control.value === input.controlValue;
        }
        if (input instanceof InputCheckboxComponent && input.controlValue() !== undefined) {
            return Array.isArray(input.control.value) && (input.control.value as string[]).includes(input.controlValue());
        }
        if (input.control.value === true) {
            return true;
        }
        return false;
    }

    private bindSetActive(input: InputCheckboxComponent | InputRadioComponent): void {
        this.updateBadgeState(input);

        // Recompute on both value changes and programmatic writes (setValue), so badges stay in
        // sync even when the control is patched with { emitEvent: false } (which valueChanges
        // suppresses).
        merge(input.control.valueChanges, input.valueWritten$).pipe(
            tap(() => this.updateBadgeState(input)),
            takeUntil(this.unsubscribe$)
        ).subscribe();
    }

    private updateBadgeState(input: InputCheckboxComponent | InputRadioComponent): void {
        this.classes['btn-toggled'] = this.classes['badge'] ? false : this.isActive(input);
        // Restricting to badges for now, as 'active' does not show up
        // as well on our standard buttons.
        this.classes['active'] = this.classes['badge'] ? this.isActive(input) : false;
        if (input.inputType === 'checkbox' && this.classes['badge']) {
            this.selectionIcon = this.isActive(input) ? faCircleCheck : faCircle;
        }
    }

    private validateConfiguration(): string[] {
        const errors: string[] = [];

        if (!this.input) {
            errors.push('No associated input has been passed in to this label');
        }

        errors.forEach((error) => {
            this.logService.error(error);
        });

        return errors;
    }


    get labelClasses(): Record<string, boolean> {
        return {
            ...this.initClasses,
            ...this.classes,
            'disabled': Boolean(this.input?.control?.disabled)
        };
    }

    get for(): string | undefined {
        return this.input?.id;
    }

    get title(): string | undefined {
        return this.input?.title;
    }
}
